import React, { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  PhotoIcon,
  TagIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  EnvelopeIcon,
  InboxIcon,
  DocumentArrowUpIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  PaperAirplaneIcon,
  ChevronDownIcon,
  EllipsisHorizontalIcon,
  BuildingLibraryIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';

interface SubmenuItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  requiredRoles?: string[];
  requiresInbox?: boolean;
  external?: boolean;
}

const submenuItems: SubmenuItem[] = [
  { path: '/ingest', label: 'Ingest', icon: DocumentArrowUpIcon },
  { path: '/inbox', label: 'Inbox', icon: InboxIcon, requiresInbox: true },
  { path: '/ims-queue', label: 'IMS', icon: DocumentArrowDownIcon },
  { path: '/results', label: 'Results', icon: DocumentTextIcon },
  { path: '/submissions', label: 'Submissions', icon: PaperAirplaneIcon },
  { path: '/wrp/triage', label: 'WRP Triage', icon: FunnelIcon },
  { path: '/photos', label: 'Photos', icon: PhotoIcon },
  { path: '/label', label: 'Label', icon: TagIcon },
  {
    path: '/failure-queue',
    label: 'Failure Queue',
    icon: ExclamationTriangleIcon,
    requiredRoles: ['admin', 'super_admin', 'manager']
  },
  {
    path: '/user-details',
    label: 'User Details',
    icon: UsersIcon,
    requiredRoles: ['manager', 'admin', 'super_admin']
  },
  {
    path: '/carrier-intelligence',
    label: 'Carrier Intelligence',
    icon: BuildingLibraryIcon,
    requiredRoles: ['manager', 'admin', 'super_admin']
  },
  {
    path: '/email-campaigns',
    label: 'Email Campaigns',
    icon: EnvelopeIcon,
    requiredRoles: ['super_admin']
  },
  {
    path: 'https://www.umbrellavision.com/api/docs/',
    label: 'API Documentation',
    icon: DocumentTextIcon,
    external: true
  },
];

interface MoreSubmenuProps {
  collapsed: boolean;
  onMobileClose?: () => void;
}

export const MoreSubmenu: React.FC<MoreSubmenuProps> = ({ 
  collapsed, 
  onMobileClose 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();

  // Filter items based on user role and org settings
  const visibleItems = submenuItems.filter(item => {
    if (item.requiresInbox && !currentOrganization?.has_inbox) return false;
    if (item.requiredRoles && !item.requiredRoles.includes(user?.role || '')) return false;
    return true;
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        setIsOpen(!isOpen);
        break;
      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
        } else {
          setFocusedIndex(prev => 
            prev < visibleItems.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) {
          setFocusedIndex(prev => 
            prev > 0 ? prev - 1 : visibleItems.length - 1
          );
        }
        break;
    }
  };

  const handleItemClick = () => {
    setIsOpen(false);
    setFocusedIndex(-1);
    onMobileClose?.();
  };

  // Collapsed state - show icon only with tooltip
  if (collapsed) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          className="group flex items-center justify-center px-0 py-3 text-white/80 hover:bg-white/10 hover:text-white transition-all duration-200 w-full"
          title="More"
          aria-label="More menu"
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <EllipsisHorizontalIcon className="w-5 h-5 mx-auto transition-transform duration-200 group-hover:translate-x-0.5" />
        </button>

        {/* Collapsed dropdown */}
        {isOpen && (
          <div className="absolute left-full top-0 ml-2 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden z-50">
            <div className="py-1">
              {visibleItems.map((item, index) => {
                const Icon = item.icon;
                
                if (item.external) {
                  return (
                    <a
                      key={item.path}
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleItemClick}
                      className={clsx(
                        'flex items-center gap-3 px-4 py-2 text-sm text-white/80 hover:bg-gray-700 hover:text-white transition-colors',
                        focusedIndex === index && 'bg-gray-700'
                      )}
                      onFocus={() => setFocusedIndex(index)}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span>{item.label}</span>
                    </a>
                  );
                }
                
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={handleItemClick}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center gap-3 px-4 py-2 text-sm text-white/80 hover:bg-gray-700 hover:text-white transition-colors',
                        isActive && 'bg-gray-700 text-white',
                        focusedIndex === index && 'bg-gray-700'
                      )
                    }
                    onFocus={() => setFocusedIndex(index)}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Expanded state
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className="group flex items-center justify-between w-full px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white transition-all duration-200"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="flex items-center">
          <EllipsisHorizontalIcon className="w-5 h-5 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" />
          <span className="ml-3">More</span>
        </div>
        <ChevronDownIcon 
          className={clsx(
            'w-4 h-4 transition-transform duration-200',
            isOpen && 'rotate-180'
          )} 
        />
      </button>

      {/* Expanded dropdown */}
      {isOpen && (
        <div className="mx-4 mb-2 bg-black/20 rounded-lg border border-white/10 overflow-hidden">
          {visibleItems.map((item, index) => {
            const Icon = item.icon;
            
            if (item.external) {
              return (
                <a
                  key={item.path}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleItemClick}
                  className={clsx(
                    'group flex items-center px-4 py-2.5 text-white/70 hover:bg-white/5 hover:text-white transition-all duration-200',
                    'border-l-2 border-transparent hover:border-white/30',
                    focusedIndex === index && 'bg-white/5'
                  )}
                  onFocus={() => setFocusedIndex(index)}
                >
                  <Icon className="w-4 h-4 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" />
                  <span className="ml-3 text-sm">{item.label}</span>
                </a>
              );
            }
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={handleItemClick}
                className={({ isActive }) =>
                  clsx(
                    'group flex items-center px-4 py-2.5 text-white/70 hover:bg-white/5 hover:text-white transition-all duration-200',
                    'border-l-2 border-transparent hover:border-white/30',
                    isActive && 'bg-white/10 text-white border-l-2 border-white/60',
                    focusedIndex === index && 'bg-white/5'
                  )
                }
                onFocus={() => setFocusedIndex(index)}
              >
                <Icon className="w-4 h-4 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" />
                <span className="ml-3 text-sm">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
};
